// src/components/JoinPresale.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Wallet, QrCode } from "lucide-react";
import { Keypair } from "@solana/web3.js";
import { encodeURL } from "@solana/pay";
import QRCode from "qrcode";
import useSolPrice from "../hooks/useSolPrice";
import Decimal from "decimal.js";

import {
  PublicKey,
  SystemProgram,
  Transaction,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";

import {
  getAssociatedTokenAddress,
  createTransferInstruction,
} from "@solana/spl-token";

import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";

export default function JoinPresale() {
  // ======================
  // CONFIG
  // ======================
  const TARGET_USDT = 10_000;
  const RAISED_USDT = 8_452.62;

  const minSol = 0.5;
  const minUsdt = 100;

  // =====================
  // RESET PAYMENT
  // =====================
  const resetPaymentState = () => {
  setAmount("");
  setPaymentStatus(null);
  setPaymentMessage("");
  setConfirming(false);
  setVerifying(false);
  setQrReference(null);
  setQrImage(null);
  setShowQRStep(false);   // hide QR step
  setShowQRModal(false);   // hide QR modal
  setMethod("SOL");        // reset to default
};


// ======================
  // WALLET ADAPTER
  // ======================
  const { connection } = useConnection();
  const { publicKey, sendTransaction, connected, disconnect } = useWallet();
  const { setVisible } = useWalletModal();

  // ======================
// WALLET CONNECT HANDLER
// ======================
const handleConnect = () => {
  setVisible(true); // must be triggered by user interaction
};

  const [countdown, setCountdown] = useState({
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
});


  // PAYMENT STATUS
const [paymentStatus, setPaymentStatus] = useState(null);
// "waiting" | "verifying" | "success" | "error"

const [paymentMessage, setPaymentMessage] = useState("");


  const TREASURY = new PublicKey("FmexP5vyCAqHJzsZ9FWq54UCjEuTHq7CgkaJ5Tnr7di1");

  const USDT_MINT = useMemo(
  () => new PublicKey("Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"),
  []
);

  // ======================
// FETCH USDT BALANCE
// ======================
const fetchUSDTBalance = useCallback(
  async (owner) => {
    try {
      const ata = await getAssociatedTokenAddress(
        USDT_MINT,
        owner
      );

      const res = await connection.getTokenAccountBalance(ata);
      return res.value.uiAmount || 0;
    } catch {
      // Wallet has no USDT account
      return 0;
    }
  },
  [connection, USDT_MINT]
);


  // store the QR reference for verification
const [qrReference, setQrReference] = useState(null);
const [verifying, setVerifying] = useState(false);

// ======================
  // Log Purchase
  // ======================
const logPurchase = async ({
  wallet,
  method,
  usdAmount,
  fmexAmount,
  txSignature,
}) => {
  await fetch("/.netlify/functions/logPurchase", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      wallet,
      method,
      usdAmount,
      fmexAmount,
      txSignature,
    }),
  });
};

// FETCH //
const fetchUserFMEX = async (walletAddress) => {
  try {
    const res = await fetch(
      `/.netlify/functions/getPurchases?wallet=${walletAddress}`
    );

    const data = await res.json();

    if (!Array.isArray(data)) return;

    const totalFMEX = data.reduce(
      (sum, p) => sum + Number(p.fmex_amount || 0),
      0
    );

    setPurchasedFMEX(totalFMEX);
    setStakeableFMEX(totalFMEX);
  } catch (err) {
    console.error("Failed to fetch FMEX balance:", err);
  }
};

  // QR CODE
const [qrImage, setQrImage] = useState(null);

  // ======================
  // STATE
  // ======================
  const [balances, setBalances] = useState({ sol: 0, usdt: 0 });
  const [method, setMethod] = useState("SOL");
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);

  const [purchasedFMEX, setPurchasedFMEX] = useState(0);
  const [stakeableFMEX, setStakeableFMEX] = useState(0);

  const [showInfoPurchased, setShowInfoPurchased] = useState(false);
  const [showInfoStakeable, setShowInfoStakeable] = useState(false);

  const [showQRStep, setShowQRStep] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const solPrice = useSolPrice();


  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : n;

  // ======================
  // DERIVED
  // ======================
  const amtNum = parseFloat(amount || 0);

const usdValue = useMemo(() => {
  if (!amtNum || amtNum <= 0) return 0;

  if (method === "SOL") {
    return solPrice ? amtNum * solPrice : 0;
  }

  return amtNum;
}, [amtNum, method, solPrice]);

 // FMEX price (single source of truth)
const FMEX_PRICE = 0.0005;

// FMEX received
const tokens = useMemo(() => {
  if (!amtNum || amtNum <= 0) return 0;

  if (method === "SOL") {
    if (!solPrice) return 0;
    return (amtNum * solPrice) / FMEX_PRICE;
  }

  // USDT
  return amtNum / FMEX_PRICE;
}, [amtNum, method, solPrice]);



  const percentRaised = Math.min(
    (RAISED_USDT / TARGET_USDT) * 100,
    100
  );

  const shortAddress = publicKey
    ? `${publicKey.toString().slice(0, 4)}...${publicKey
        .toString()
        .slice(-4)}`
    : "";

const hasLowSol = balances.sol < minSol;
const hasLowUsdt = balances.usdt < minUsdt;

  // ======================
  // SOL PRICE (REAL-TIME)
  // ======================


  // ======================
  // Countdown
  // ======================
  useEffect(() => {
  const target = new Date("2026-10-15T23:59:00Z").getTime();

  const interval = setInterval(() => {
    const diff = Math.max(0, target - Date.now());

    setCountdown({
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    });
  }, 1000);

  return () => clearInterval(interval);
}, []);

  // ======================
// BALANCES (SOL + USDT)
// ======================
useEffect(() => {
  if (!publicKey) return;

  (async () => {
    try {
      const lamports = await connection.getBalance(publicKey);
      const usdt = await fetchUSDTBalance(publicKey);

      setBalances({
        sol: lamports / LAMPORTS_PER_SOL,
        usdt,
      });
    } catch (err) {
      console.error("Failed to fetch balances:", err);
    }
  })();
}, [publicKey, fetchUSDTBalance, connection]);


  // FETCH USE //
  useEffect(() => {
  if (!publicKey) {
    setPurchasedFMEX(0);
    setStakeableFMEX(0);
    return;
  }

  fetchUserFMEX(publicKey.toString());
}, [publicKey]);


  // ======================
// QR GENERATE (SOLANA PAY - SOL)
// ======================
const generateSolanaPayQR = async () => {
  setPaymentStatus(null);
  setPaymentMessage("");

  if (!amount || Number(amount) < minSol) {
    setPaymentStatus("error");
    setPaymentMessage(`Minimum ${minSol} SOL required.`);
    return;
  }

  try {
    const reference = Keypair.generate().publicKey;
    setQrReference(reference);

    const url = encodeURL({
      recipient: TREASURY,
      amount: new Decimal(amount),
      splToken: undefined,
      reference: [reference],
      label: "FMEX Presale",
      message: "FMEX Token Purchase",
      cluster: "mainnet-beta",
    });

    const qr = await QRCode.toDataURL(url.toString(), {
      width: 512,
      margin: 1,
    });

    setQrImage(qr);
    setShowQRModal(true);

    setPaymentStatus("waiting");
    setPaymentMessage("Scan the QR code and complete payment in your wallet.");
  } catch (err) {
    console.error(err);
    setPaymentStatus("error");
    setPaymentMessage("Failed to generate QR code.");
  }
};

// ======================
// QR REFERENCE
// ======================
const [txSignature, setTxSignature] = useState(null);

const verifySolanaPayPayment = async () => {
  if (!qrReference) return;

  setVerifying(true);
  setPaymentStatus("verifying");
  setPaymentMessage("Checking blockchain confirmation…");

  try {
    const res = await fetch("/.netlify/functions/verifyQrPayment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reference: qrReference.toString(),
        amount,
        treasury: TREASURY.toString(),
        usdValue,
        fmexAmount: Math.round(tokens),
      }),
    });

    const data = await res.json();

    if (!data.success) throw new Error(data.error);

    setPaymentStatus("success");
    setPaymentMessage(
      `Payment confirmed! You will only need your wallet to claim your FMEX later.`
    );

    setTxSignature(data.signature); // store transaction signature
    setQrImage(null); // hide QR

    setPurchasedFMEX((p) => p + data.fmexAmount);
    setStakeableFMEX((p) => p + data.fmexAmount);
  } catch (err) {
    setPaymentStatus("error");
    setPaymentMessage(
      "Payment not found yet. Please wait a few seconds and try again."
    );
  }

  setVerifying(false);
};


  // ======================
// BUY WITH SOL
// ======================
const buyWithSOL = async () => {
  if (!publicKey) {
    setPaymentStatus("error");
    setPaymentMessage("Connect your wallet first.");
    return;
  }

  if (!amtNum || amtNum < minSol) {
    setPaymentStatus("error");
    setPaymentMessage(`Minimum ${minSol} SOL required.`);
    return;
  }

  if (balances.sol < amtNum) {
    setPaymentStatus("error");
    setPaymentMessage("Insufficient SOL balance.");
    return;
  }

  setConfirming(true);
  setPaymentStatus("pending");
  setPaymentMessage("Processing SOL transaction...");

  const fmexAmount = Math.round(tokens);

  try {
    const tx = new Transaction().add(
      SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: TREASURY,
        lamports: amtNum * LAMPORTS_PER_SOL,
      })
    );

    const sig = await sendTransaction(tx, connection);
    await connection.confirmTransaction(sig, "confirmed");

    // ✅ Log purchase in Supabase
    try {
      await logPurchase({
        wallet: publicKey.toString(),
        method: "SOL",
        usdAmount: usdValue,
        fmexAmount,
        txSignature: sig,
      });
    } catch (logErr) {
      console.error("Logging failed:", logErr);
    }

    setPurchasedFMEX((p) => p + fmexAmount);
    setStakeableFMEX((p) => p + fmexAmount);
    setAmount("");

    setPaymentStatus("success");
    setPaymentMessage(
      `Payment confirmed! You receive ${fmexAmount.toLocaleString()} FMEX.`
    );
  } catch (err) {
    console.error(err);

    if (err?.message?.toLowerCase().includes("user rejected") || err?.code === 4001) {
      setPaymentStatus("error");
      setPaymentMessage("Transaction cancelled in wallet.");
    } else if (err?.message?.toLowerCase().includes("insufficient") || err?.message?.toLowerCase().includes("funds")) {
      setPaymentStatus("error");
      setPaymentMessage("Insufficient SOL balance.");
    } else {
      setPaymentStatus("error");
      setPaymentMessage("SOL transaction failed. Try again.");
    }
  }

  setConfirming(false);
};

// ======================
// BUY WITH USDT
// ======================
const buyWithUSDT = async () => {
  if (!publicKey) {
    setPaymentStatus("error");
    setPaymentMessage("Connect your wallet first.");
    return;
  }

  if (!amtNum || amtNum < minUsdt) {
    setPaymentStatus("error");
    setPaymentMessage(`Minimum ${minUsdt} USDT required.`);
    return;
  }

  if (balances.usdt < amtNum) {
    setPaymentStatus("error");
    setPaymentMessage("Insufficient USDT balance.");
    return;
  }

  setConfirming(true);
  setPaymentStatus("pending");
  setPaymentMessage("Processing USDT transaction...");

  const fmexAmount = Math.round(tokens);

  try {
    const buyerATA = await getAssociatedTokenAddress(USDT_MINT, publicKey);
    const treasuryATA = await getAssociatedTokenAddress(USDT_MINT, TREASURY, true);

    const ix = createTransferInstruction(
      buyerATA,
      treasuryATA,
      publicKey,
      amtNum * 1_000_000 // USDT decimals
    );

    const tx = new Transaction().add(ix);
    const sig = await sendTransaction(tx, connection);
    await connection.confirmTransaction(sig, "confirmed");

    // ✅ Log purchase in Supabase
    try {
      await logPurchase({
        wallet: publicKey.toString(),
        method: "USDT",
        usdAmount: amtNum,
        fmexAmount,
        txSignature: sig,
      });
    } catch (logErr) {
      console.error("Logging failed:", logErr);
    }

    setPurchasedFMEX((p) => p + fmexAmount);
    setStakeableFMEX((p) => p + fmexAmount);
    setAmount("");

    setPaymentStatus("success");
    setPaymentMessage(
      `Payment confirmed! You receive ${fmexAmount.toLocaleString()} FMEX.`
    );
  } catch (err) {
    console.error(err);

    if (err?.message?.toLowerCase().includes("user rejected") || err?.code === 4001) {
      setPaymentStatus("error");
      setPaymentMessage("Transaction cancelled in wallet.");
    } else if (err?.message?.toLowerCase().includes("insufficient") || err?.message?.toLowerCase().includes("funds")) {
      setPaymentStatus("error");
      setPaymentMessage("Insufficient USDT balance.");
    } else {
      setPaymentStatus("error");
      setPaymentMessage("USDT transaction failed. Try again.");
    }
  }

  setConfirming(false);
};



// Payment Status
const PaymentStatusBox = ({ status, message }) => {
  if (!status) return null;

  const styles = {
    waiting: "bg-yellow-100 border-yellow-400 text-yellow-900",
    verifying: "bg-blue-100 border-blue-400 text-blue-900",
    success: "bg-green-100 border-green-400 text-green-900",
    error: "bg-red-100 border-red-400 text-red-900",
  };

  return (
    <div
      className={`mt-3 border rounded-lg p-3 text-xs font-comfortaa font-bold ${styles[status]}`}
    >
      {message}
    </div>
  );
};

  return (
    <section id="presale"
      className="relative min-h-[90vh] md:min-h-[105vh] bg-cover bg-left md:bg-center flex items-center justify-center">
        <div className="max-w-7xl w-full px-6 flex flex-col md:flex-row items-center justify-between gap-10">
        {/* LEFT empty side (for spacing) */}
        <div className="flex-1" />

        {/* RIGHT CARD */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, x: 100, scale: 0.95 }}
          animate={
            inView
              ? { opacity: 1, x: 0, scale: 1 }
              : { opacity: 0, x: 100, scale: 0.95 }
          }
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full md:w-[480px] bg-white border-4 border-black rounded-2xl shadow-2xl py-8 p-6"
        >
          <div className="flex items-center gap-6 mb-3">
  <h2
    className="text-[27px] md:text-4xl font-extrabold text-black"
    style={{ fontFamily: "Darumadrop One, cursive" }}
  >
    BUY $FMEX NOW
  </h2>

  {/* 🔥 Stage 1 Tag */}
  <span className="text-sm md:text-base bg-yellow-300 border-2 border-black rounded-lg px-2 py-1 font-bold font-darumadrop comic-shadow">
    Stage 1
  </span>
</div>
          <p className="text-xs md:text-sm font-comfortaa font-bold md:font-semibold text-gray-700 mb-4">
            You’re early. Grab FMEX now for 40x less before your ex does.
          </p>

          {/* Progress */}
<div className="mb-5">
  <div className="relative h-5 w-full bg-gray-200 rounded-full overflow-hidden border border-gray-400">
    
    {/* Filled area */}
    <div
      className="relative h-full overflow-hidden"
      style={{ width: `${percentRaised}%` }}
    >
      {/* Main gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-yellow-400 to-purple-500"></div>

      {/* Moving comic stripes */}
      <div className="
        absolute inset-0 opacity-40
        bg-[linear-gradient(45deg,white_25%,transparent_25%,transparent_50%,white_50%,white_75%,transparent_75%,transparent)]
        bg-[length:20px_20px]
        animate-moveStripes
      "></div>
    </div>

    {/* ⭐️ Text inside progress bar */}
    <div className="
      absolute inset-0 
      flex items-center justify-center 
      text-sm font-bold font-comicn text-black drop-shadow
    ">
     Stage 1 - {percentRaised.toFixed(0)}%
    </div>
  </div>

  <div className="flex justify-between text-xs mt-2 text-gray-700 font-bold">
    <span>${fmt(RAISED_USDT)} / $500,000 USDT Raised</span>
    <span>{percentRaised.toFixed(2)}%</span>
  </div>
</div>



          {/* Countdown */}
          <div className="flex justify-between items-center bg-white/70 border border-black rounded-lg p-3 mb-6">
            <span className="font-bold text-[11px] md:text-sm text-gray-800">
              Price Increases In:
            </span>
            <div className="flex gap-2 text-center">
              {["days", "hours", "minutes", "seconds"].map((k, i) => (
                <div
                  key={i}
                  className="bg-black text-white font-bold px-2 py-1 rounded-lg w-8 md:w-10"
                >
                  {String(countdown[k] || 0).padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>

            <div className="relative flex flex-col items-center justify-center mb-4">
  {/* Top row */}
  <div className="relative flex items-center justify-center w-full mb-1">
    <div className="absolute left-0 w-1/12 md:w-1/6 h-px bg-gray-400"></div>

    <span className="flex items-center gap-1 font-comfortaa font-black text-sm md:text-base text-black">
      <img 
        src="/images/fmex.png" 
        alt="FMEX Logo" 
        className="w-7 h-7 object-contain"
      />
      1 $FMEX = 0.0005 USD
    </span>

    <div className="absolute right-0 w-1/12 md:w-1/6 h-px bg-gray-400"></div>
  </div>

  {/* Next Stage Price */}
  <span className="text-xs font-bold font-comfortaa text-gray-600">
    Next Stage Price: 0.001 USD
  </span>
</div>

{/* User Balances After Connect */}
{connected && (
  <div className="mt-4 space-y-3">

    {/* Purchased FMEX */}
    <div className="flex items-center justify-between bg-white/80 border border-black rounded-lg px-3 py-2">
      <span className="font-comfortaa font-bold text-sm">
        Your purchased $FMEX = {purchasedFMEX.toLocaleString()}
      </span>

      {/* Info Icon */}
      <img
        src="/images/info.png" alt="info"
        className="w-5 h-5 cursor-pointer"
        onClick={() => setShowInfoPurchased(!showInfoPurchased)}
      />
    </div>

    {/* Purchased FMEX Tooltip */}
    {showInfoPurchased && (
      <div className="text-xs bg-yellow-100 border border-black rounded-lg p-3 font-comfortaa">
        Your purchased balance is the total number of tokens you've purchased
        using the connected wallet. This includes all staked and unstaked tokens.
      </div>
    )}

    {/* Stakeable FMEX */}
    <div className="flex items-center justify-between bg-white/80 border border-black rounded-lg px-3 py-2">
      <span className="font-comfortaa font-bold text-sm">
        Your stakeable $FMEX = {stakeableFMEX.toLocaleString()}
      </span>

      {/* Info Icon */}
      <img
        src="/images/info.png" alt="info"
        className="w-5 h-5 cursor-pointer"
        onClick={() => setShowInfoStakeable(!showInfoStakeable)}
      />
    </div>

    {/* Stakeable FMEX Tooltip */}
    {showInfoStakeable && (
      <div className="text-xs bg-green-100 border border-black rounded-lg p-3 font-comfortaa">
        Your stakeable balance is the number of purchased tokens that can still
        be staked during the presale period.
      </div>
    )}

  </div>
)}


          {/* Wallet connect / buy */}
          {!connected && (
<div className="flex flex-col gap-3">

  
    <button
      onClick={handleConnect}
      className="flex items-center justify-center gap-2 py-3 bg-black text-white font-darumadrop text-lg border-[2px] border-white rounded-xl hover:scale-105 transition-transform w-full"
    >
      <Wallet size={20} />
      Connect Wallet
    </button>

  <button
    onClick={() => setShowQRStep(true)}
    className="flex items-center justify-center gap-2 py-3 bg-hotpink text-white font-darumadrop text-lg border-[2px] border-black rounded-xl hover:scale-105 transition-transform"
  >
    <QrCode />
    Scan QR Code to Buy
  </button>

</div>

)}
          

          {connected && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 space-y-4"
            >
              {method === "SOL" && hasLowSol && (
  <div className="bg-red-100 border border-red-400 text-red-800 rounded-lg p-3 text-xs font-bold font-comfortaa">
    ⚠️ Your SOL balance is too low.
    <br />
    Minimum required: {minSol} SOL
  </div>
)}

{method === "USDT" && hasLowUsdt && (
  <div className="bg-red-100 border border-red-400 text-red-800 rounded-lg p-3 text-xs font-bold font-comfortaa">
    ⚠️ Your USDT balance is too low.
    <br />
    Minimum required: {minUsdt} USDT
  </div>
)}

              <div className="flex justify-between text-sm">
                <span className="font-bold font-commfortaa">{shortAddress}</span>
                <span className="text-gray-700 font-comfortaa">
                  SOL: {balances.sol} | USDT: {balances.usdt}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2">
  <button
  onClick={() => {
    resetPaymentState();
    setMethod("SOL");
  }}
  className={`flex items-center justify-center gap-2 py-2 rounded-lg font-bold font-comfortaa border-2 transition ${
    method === "SOL"
      ? "bg-black text-white border-black"
      : "bg-white border-gray-300"
  }`}
>
  <img src="/icons/solicon.svg" alt="Solana" className="w-5 h-5" />
  <span>SOL</span>
</button>

<button
  onClick={() => {
    resetPaymentState();
    setMethod("USDT");
  }}
  className={`flex items-center justify-center gap-2 py-2 rounded-lg font-bold font-comfortaa border-2 transition ${
    method === "USDT"
      ? "bg-black text-white border-black"
      : "bg-white border-gray-300"
  }`}
>
  <img src="/icons/usdticon.svg" alt="USDT" className="w-5 h-5" />
  <span>USDT</span>
</button>
  </div>

              <div className="bg-white/90 p-4 rounded-xl border border-gray-400">
                <label className="font-comfortaa font-semibold text-xs text-gray-600">Amount ({method})</label>
                {method === "SOL" && solPrice && amount && (
  <div className="text-xs text-gray-600 mt-1">
    ≈ ${(amount * solPrice).toFixed(2)} USD
  </div>
)}
                <input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 mt-2"
                  placeholder={method === "SOL" ? "Min 0.5 SOL" : "Min 100 USDT"}
                />
                <div className="mt-2 text-sm text-gray-800 font-comfortaa flex items-center gap-2">
                  You’ll receive:
                  <span className="font-bold">
                    {" "}
                    {tokens ? Math.round(tokens).toLocaleString() : "-"} $FMEX
                  </span>  <img src="/images/fmex.png" alt="FMEX" className="w-5 h-5" />
                </div>
              </div>

              <button
  onClick={method === "SOL" ? buyWithSOL : buyWithUSDT}
  disabled={!amount || confirming}
  className="w-full py-3 bg-pink-500 text-white font-bold font-comfortaa rounded-xl hover:scale-105 transition disabled:opacity-50"
>
  {confirming ? "Processing..." : `Buy with ${method}`}
</button>

              {paymentStatus && (
  <PaymentStatusBox
    status={paymentStatus}
    message={paymentMessage}
  />
)}

              <div
  onClick={() => {
    disconnect();
    resetPaymentState();
  }}
  className="underline flex items-center justify-center hover:text-yellow-800 text-sm font-comfortaa font-bold cursor-pointer transition-colors"
>
  Disconnect Wallet
</div>
            </motion.div>
          )}

          {/* QR Step */}
{showQRStep && !showQRModal && !connected && (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-6 bg-white/90 border border-gray-400 rounded-xl p-4"
  >
    <div className="font-bold font-comfortaa text-sm mb-2">
      Enter SOL amount before scanning:
    </div>
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="number"
          value={amount}
          min={0}
          onChange={(e) => {
            const val = e.target.value;
            setAmount(val);

            // Reset error if amount is valid
            if (Number(val) >= minSol) {
              setPaymentStatus(null);
              setPaymentMessage("");
            }
          }}
          placeholder={`Min ${minSol} SOL`}
          className="flex-1 px-3 py-2 border rounded-lg"
        />
        <button
          onClick={generateSolanaPayQR}
          className="px-3 py-2 bg-black text-xs text-white font-bold rounded-lg"
        >
          Pay with QR
        </button>
      </div>

      {/* Live error message */}
      {paymentStatus === "error" && paymentMessage && (
        <div className="text-red-600 text-xs font-bold font-comfortaa">
          {paymentMessage}
        </div>
      )}

      {/* 💰 Live preview */}
      {amount > 0 && solPrice && (
        <div className="text-xs font-comfortaa text-gray-700 bg-white/80 border border-gray-300 rounded-lg p-2 space-y-1">
          <div>
            ≈ <b>${usdValue.toFixed(2)}</b> USD
          </div>

          <div className="flex items-center gap-1 font-bold font-comfortaa text-black">
            You receive:
            <span className="text-pink-600">
              {Math.round(tokens).toLocaleString()} FMEX
            </span>
            <img src="/images/fmex.png" alt="fmex" className="w-4 h-4" />
          </div>
        </div>
      )}

      {!solPrice && amount > 0 && (
        <div className="text-xs text-gray-500">
          Loading price…
        </div>
      )}
    </div>
  </motion.div>
)}
        </motion.div>
      </div>

      <AnimatePresence>
  {showQRModal && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={() => {
          setShowQRModal(false);
          resetPaymentState();
        }}
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative z-10 bg-white rounded-2xl p-6 max-w-sm w-full border-2 border-black"
      >
        <div className="text-center font-bold font-comfortaa mb-4 text-lg">
          {paymentStatus === "success" ? "Payment Confirmed!" : "Scan to Buy $FMEX"}
        </div>

        {qrImage && (
          <img
            src={qrImage}
            alt="Solana Pay QR"
            className="w-64 h-64 mx-auto"
          />
        )}

        {!qrImage && paymentStatus === "success" && (
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm font-comfortaa text-center text-gray-700">
              {paymentMessage}
            </p>
            {txSignature && (
              <a
                href={`https://explorer.solana.com/tx/${txSignature}?cluster=mainnet-beta`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700"
              >
                View Transaction
              </a>
            )}
          </div>
        )}

        {qrImage && (
          <p className="scan-comfortaa text-xs text-center text-gray-600 mt-3">
            Scan with Phantom, Solflare, or any Solana Pay wallet
          </p>
        )}

        <div className="text-sm font-comfortaa text-gray-700 space-y-1 mb-3">
          <p>
            You pay: <b>{amount || "—"}</b> SOL
          </p>

          {solPrice && tokens > 0 && (
            <p className="font-bold text-black flex items-center justify-center gap-1">
              You receive:
              <span className="text-pink-600">
                {Math.round(tokens).toLocaleString()} FMEX
              </span>
              <img src="/images/fmex.png" alt="fmex" className="w-4 h-4" />
            </p>
          )}
        </div>

        {qrImage && (
          <button
            onClick={verifySolanaPayPayment}
            disabled={verifying}
            className="w-full py-2 bg-black text-white font-bold rounded-lg disabled:opacity-50"
          >
            {verifying ? "Verifying..." : "I’ve Paid"}
          </button>          
        )}
        {paymentStatus === "error" && (
  <PaymentStatusBox
    status={paymentStatus}
    message={paymentMessage}
  />
)}
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>


    </section>
  );
}
